// SPDX-License-Identifier: Apache-2.0
import GirafeSingleton from '../../base/GirafeSingleton';
import BaseLayer from '../../models/layers/baselayer';
import GroupLayer from '../../models/layers/grouplayer';
import ThemeLayer from '../../models/layers/themelayer';

type OrderCounter = {
  index: number;
};

export const LayerTreeStartOrder = 1000;

export default class OrderingManager extends GirafeSingleton {
  private timeoutId?: NodeJS.Timeout;
  private isReordering = false;

  private get state() {
    return this.context.stateManager.state;
  }

  public override async initializeSingleton() {
    this.registerEvents();
  }

  private registerEvents() {
    this.context.stateManager.subscribe('layers.layersList', (oldLayers: BaseLayer[], newLayers: BaseLayer[]) =>
      this.onLayerListChanged(oldLayers, newLayers)
    );
    this.context.stateManager.subscribe(
      /layers\.layersList\..*\.children/,
      (oldLayers: BaseLayer[], newLayers: BaseLayer[]) => this.onLayerListChanged(oldLayers, newLayers)
    );
    this.context.stateManager.subscribe(/layers\.layersList\..*\.order/, () => {
      if (!this.isReordering) {
        this.reorderLayers();
      }
    });
  }

  private onLayerListChanged(oldLayers: BaseLayer[], newLayers: BaseLayer[]) {
    // Need to reorder only if new layers were added.
    const oldTreeItemIds = new Set(oldLayers.map((oldChild) => oldChild.treeItemId));
    const hasAddedLayers = newLayers.some((newChild) => !oldTreeItemIds.has(newChild.treeItemId));
    if (hasAddedLayers) {
      this.reorderLayers();
    }
  }

  /**
   * This method do a reorder of all layers present in the treeview
   * in order to keep a order attribut corresponding to what the use wants.
   * This order will be used by the map component to calculate the list of layers in the right order
   */
  private reorderLayers() {
    // Use a debouncing to prevent multiple execution of this method
    // If multiple order attributes are modified at the same time.
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.timeoutId = undefined;
      this.isReordering = true;
      const wasRenderEnabled = this.state.treeview.renderEnabled;
      if (wasRenderEnabled) {
        this.state.treeview.renderEnabled = false;
      }
      try {
        const orderedLayers = this.getSortedLayers(this.state.layers.layersList);
        const counter: OrderCounter = { index: LayerTreeStartOrder };
        this.context.stateManager.batchChanges(() => this.reorderLayersRecursively(orderedLayers, counter));
      } finally {
        if (wasRenderEnabled) {
          this.state.treeview.renderEnabled = true;
        }
        this.isReordering = false;
      }
    });
  }

  private reorderLayersRecursively(orderedLayers: BaseLayer[], counter: OrderCounter) {
    // Traverse the layertree in depth first to renumber all the elements
    for (const layer of orderedLayers) {
      // NOTE REG : Setting a variable to the very same value is managed by brain in theory,
      // and no event will be sent by brain is the value is identical.
      // But even if the call to a proxy has a small cost, here during a reorder, it can be called many times
      // Because we reorder the whole treeview, which can be very big.
      // Therefore, it is more performant here to add this test before setting the variable to the same value.
      const nextOrder = counter.index++;
      if (layer.order !== nextOrder) {
        layer.order = nextOrder;
      }
      if (layer instanceof GroupLayer || layer instanceof ThemeLayer) {
        const orderedChilds = this.getSortedLayers(layer.children);
        this.reorderLayersRecursively(orderedChilds, counter);
      }
    }
  }

  private getSortedLayers(layers: BaseLayer[]): BaseLayer[] {
    const orderedLayers = layers.slice().sort((l1: BaseLayer, l2: BaseLayer) => {
      // Make sure pinned layers are always on top
      if (l1.isPinned && !l2.isPinned) return -1;
      if (!l1.isPinned && l2.isPinned) return 1;
      if (l1.isPinned && l2.isPinned) return 0;
      return l1.order - l2.order;
    });
    return orderedLayers;
  }
}
